/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CommonStepper.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React from "react";
import { Box, Typography } from "@mui/material";

// Colors
const GREEN = "#26890D";
const GREY = "#53565A";

// Size constants
const STEP_SIZE = 18;
const INNER_SIZE = 14;
const CONNECTOR_HEIGHT = 5;

const CommonStepper = ({ steps = [], activeStep = 0 }) => {
  const stepCount = steps.length;

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Stepper bar and steps */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          display: "grid",
          gridTemplateColumns: `repeat(${stepCount}, 1fr)`,
          alignItems: "center",
          mb: 2,
          position: "relative",
        }}
      >
        {/* Connector line - behind steps, full width */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            height: CONNECTOR_HEIGHT,
            bgcolor: GREY,
            borderRadius: CONNECTOR_HEIGHT / 2,
            zIndex: 0,
          }}
        >
          {/* Green completed connector overlay */}
          <Box
            sx={{
              height: "100%",
              bgcolor: GREEN,
              borderRadius: CONNECTOR_HEIGHT / 2,
              transition: "width 0.3s ease",
              width:
                stepCount <= 1
                  ? "100%"
                  : activeStep === stepCount - 1
                  ? "100%" // Full width when on last step
                  : `calc((${activeStep} + 0.5) / ${stepCount} * 100%)`,
            }}
          />
        </Box>

        {/* Steps */}
        {steps.map((_, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: STEP_SIZE,
                height: STEP_SIZE,
                borderRadius: "50%",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px 0 rgba(0,0,0,0.18)",
              }}
            >
              <Box
                sx={{
                  width: INNER_SIZE,
                  height: INNER_SIZE,
                  borderRadius: "50%",
                  background: idx <= activeStep ? GREEN : GREY,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
      {/* Labels in grid, one per step */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          display: "grid",
          gridTemplateColumns: `repeat(${stepCount}, 1fr)`,
          alignItems: "start",
        }}
      >
        {steps.map((label, idx) => (
          <Typography
            key={label}
            sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "14px",
              color: "#000",
              textAlign: "center",
              lineHeight: 1.1,
              wordBreak: "break-word",
              whiteSpace: "pre-line",
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default CommonStepper;
